import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";

// Status app absensi (PWA) + posisi GPS teknisi — data dari heartbeat /presence.js di absensi.deteksipipabocor.com.
// Leaflet dimuat dari CDN saat dibutuhkan (tidak menambah ukuran bundle).
const LEAFLET_VER = "1.9.4";
let leafletPromise = null;
const loadLeaflet = () => {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.css`;
    document.head.appendChild(css);
    const js = document.createElement("script");
    js.src = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.js`;
    js.onload = () => resolve(window.L);
    js.onerror = () => { leafletPromise = null; reject(new Error("leaflet")); };
    document.body.appendChild(js);
  });
  return leafletPromise;
};

const jam = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" }); } catch (_) { return "-"; }
};
const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "baru saja";
  if (s < 3600) return `${Math.round(s / 60)} menit lalu`;
  if (s < 86400) return `${Math.round(s / 3600)} jam lalu`;
  return `${Math.round(s / 86400)} hari lalu`;
};
const gmaps = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

export const APP_STATUS = {
  online: { label: "App aktif", dot: "bg-[#059669]", text: "text-[#059669]" },
  background: { label: "App di latar belakang", dot: "bg-[#D97706]", text: "text-[#D97706]" },
  offline: { label: "App tidak aktif", dot: "bg-[#A1A1AA]", text: "text-[#52525B]" },
  never: { label: "Belum ada data app", dot: "bg-[#E4E4E7] border border-[#A1A1AA]", text: "text-[#A1A1AA]" },
};

const GPS_LABEL = {
  ok: "GPS aktif", denied: "Izin lokasi DITOLAK", timeout: "GPS timeout (sinyal lemah)", unavailable: "GPS tidak tersedia / mati",
  unsupported: "Browser tidak mendukung GPS", skip: "Izin lokasi belum diberikan",
};

const PresenceMap = ({ data }) => {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let map = null;
    let dead = false;
    loadLeaflet().then((L) => {
      if (dead || !ref.current) return;
      map = L.map(ref.current, { zoomControl: true, attributionControl: true });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(map);
      const bounds = [];
      const pin = (lat, lng, color, label, r = 7) => {
        if (lat == null || lng == null || (!lat && !lng)) return;
        L.circleMarker([lat, lng], { radius: r, color: "#fff", weight: 2, fillColor: color, fillOpacity: 1 }).addTo(map).bindPopup(label);
        bounds.push([lat, lng]);
      };
      const pings = data.pings || [];
      if (pings.length > 1) {
        L.polyline(pings.map((p) => [p.lat, p.lng]), { color: "#2563EB", weight: 3, opacity: 0.6 }).addTo(map);
        pings.forEach((p) => { L.circleMarker([p.lat, p.lng], { radius: 3, color: "#2563EB", weight: 1, fillOpacity: 0.8 }).addTo(map).bindPopup(`Jejak ${jam(p.ts)} (±${p.accuracy || "?"} m)`); bounds.push([p.lat, p.lng]); });
      }
      (data.attendance || []).forEach((a) => pin(a.lat, a.lng, a.type === "in" ? "#059669" : "#F97316", `Absen ${a.type === "in" ? "masuk" : "pulang"} ${jam(a.timestamp)} · ${a.location_name || ""}`, 8));
      (data.field || []).forEach((f) => {
        pin(f.check_in_lat, f.check_in_lng, "#0891B2", `Dinas mulai ${jam(f.check_in_at)} · ${f.task_name || ""}`);
        if (f.check_out_at) pin(f.check_out_lat, f.check_out_lng, "#0891B2", `Dinas selesai ${jam(f.check_out_at)}`);
      });
      (data.arrivals || []).forEach((t) => pin(t.lat, t.lng, "#7C3AED", `Tiba ${jam(t.arrived_at)} · ${t.customer_name || ""} (${t.order_no || ""})`, 8));
      const d = data.device;
      if (d && d.lat != null) {
        L.circleMarker([d.lat, d.lng], { radius: 11, color: "#fff", weight: 3, fillColor: "#DC2626", fillOpacity: 1 }).addTo(map)
          .bindPopup(`<b>Posisi terakhir</b><br>${jam(d.gps_at)} (${ago(d.gps_at)})<br>Akurasi ±${d.accuracy || "?"} m`).openPopup();
        if (d.accuracy) L.circle([d.lat, d.lng], { radius: d.accuracy, color: "#DC2626", weight: 1, fillOpacity: 0.08 }).addTo(map);
        bounds.push([d.lat, d.lng]);
      }
      if (bounds.length === 1) map.setView(bounds[0], 16);
      else if (bounds.length) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 17 });
      else map.setView([-2.5, 118], 4);
      setTimeout(() => map && map.invalidateSize(), 150);
    }).catch(() => !dead && setFailed(true));
    return () => { dead = true; if (map) map.remove(); };
  }, [data]);
  if (failed) {
    const d = data.device;
    return <div className="text-xs text-[#52525B] border border-dashed border-[#E4E4E7] p-3">Peta gagal dimuat.{d?.lat != null && <> <a className="text-[#002FA7] font-bold" href={gmaps(d.lat, d.lng)} target="_blank" rel="noreferrer">Buka posisi terakhir di Google Maps →</a></>}</div>;
  }
  return <div ref={ref} className="w-full h-64 sm:h-72 border border-[#E4E4E7] bg-[#F4F4F5]" data-testid="presence-map" />;
};

const TechPresence = ({ technicianId }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get(`/technicians/${technicianId}/presence`).then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "Gagal memuat status app"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [technicianId]);

  if (!data) return <div className="text-xs text-[#52525B] p-1" data-testid="presence-loading">{err || "Memuat status app absensi…"}</div>;
  const d = data.device;
  const st = APP_STATUS[data.app_status] || APP_STATUS.never;
  const gpsOk = d?.gps_status === "ok";
  const gpsFresh = d?.gps_at && Date.now() - new Date(d.gps_at).getTime() < 10 * 60e3;
  return (
    <div className="space-y-2" data-testid="presence-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="label-cap text-[10px]">Status App Absensi &amp; Lokasi GPS</div>
        <button onClick={load} disabled={loading} className="text-[10px] font-bold uppercase tracking-wider text-[#002FA7] disabled:opacity-50" data-testid="presence-refresh">{loading ? "Memuat…" : "↻ Muat ulang"}</button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="border border-[#E4E4E7] px-2.5 py-2">
          <div className="flex items-center gap-1.5 font-bold"><span className={`inline-block w-2.5 h-2.5 rounded-full ${st.dot}`} /><span className={st.text}>{st.label}</span></div>
          <div className="text-[11px] text-[#52525B] mt-0.5">
            {d ? <>Terakhir terhubung {jam(d.last_seen)} · {ago(d.last_seen)}</> : "Teknisi belum membuka app versi baru"}
          </div>
        </div>
        <div className="border border-[#E4E4E7] px-2.5 py-2">
          <div className={`font-bold ${!d ? "text-[#A1A1AA]" : gpsOk ? "text-[#059669]" : "text-[#DC2626]"}`}>{!d ? "GPS: -" : GPS_LABEL[d.gps_status] || d.gps_status || "-"}</div>
          <div className="text-[11px] text-[#52525B] mt-0.5">
            {d?.gps_at ? <>Posisi {jam(d.gps_at)} · {ago(d.gps_at)}{d.accuracy ? ` · ±${d.accuracy} m` : ""}</> : "Belum ada posisi"}
          </div>
        </div>
      </div>
      {d && (
        <div className="flex flex-wrap gap-1.5 text-[10px] font-bold uppercase tracking-wider">
          <span className={`px-1.5 py-0.5 border ${d.standalone ? "border-[#059669]/40 text-[#059669]" : "border-[#D97706]/40 text-[#D97706]"}`}>{d.standalone ? "PWA terpasang" : "Dibuka di browser"}</span>
          <span className={`px-1.5 py-0.5 border ${data.push_subscribed ? "border-[#059669]/40 text-[#059669]" : "border-[#E4E4E7] text-[#52525B]"}`}>{data.push_subscribed ? "Notifikasi aktif" : "Notifikasi belum aktif"}</span>
          {d.battery != null && <span className={`px-1.5 py-0.5 border ${d.battery <= 15 ? "border-[#DC2626]/40 text-[#DC2626]" : "border-[#E4E4E7] text-[#52525B]"}`}>Baterai {d.battery}%{d.charging ? " ⚡" : ""}</span>}
          {d.gps_perm && <span className="px-1.5 py-0.5 border border-[#E4E4E7] text-[#52525B]">Izin lokasi: {d.gps_perm}</span>}
        </div>
      )}
      {d?.lat != null && !gpsFresh && <div className="text-[11px] text-[#D97706]">Posisi bukan real-time — lokasi terakhir yang terkirim saat app dibuka.</div>}
      <PresenceMap data={data} />
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[#52525B]">
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#DC2626] mr-1" />Posisi terakhir</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#059669] mr-1" />Absen masuk</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#F97316] mr-1" />Absen pulang</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#7C3AED] mr-1" />Tiba di customer</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#2563EB] mr-1" />Jejak hari ini ({(data.pings || []).length})</span>
        {d?.lat != null && <a className="text-[#002FA7] font-bold ml-auto" href={gmaps(d.lat, d.lng)} target="_blank" rel="noreferrer" data-testid="presence-gmaps">Google Maps →</a>}
      </div>
      {(data.attendance || []).length + (data.arrivals || []).length > 0 && (
        <div className="text-[11px] text-[#52525B] border-t border-[#E4E4E7] pt-1.5 space-y-0.5">
          {(data.attendance || []).map((a, i) => (
            <div key={`a${i}`}>• Absen {a.type === "in" ? "masuk" : "pulang"} <b>{jam(a.timestamp)}</b> — {a.luar_kota ? "Luar kota/BC" : a.location_name || "-"}{a.lat ? <> · <a className="text-[#002FA7]" href={gmaps(a.lat, a.lng)} target="_blank" rel="noreferrer">peta</a></> : null}</div>
          ))}
          {(data.arrivals || []).map((t, i) => (
            <div key={`t${i}`}>• Tiba di <b>{t.customer_name || "-"}</b>{t.customer_city ? ` (${t.customer_city})` : ""} <b>{jam(t.arrived_at)}</b> · {t.distance_m} m dari titik</div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-[#A1A1AA]">Lokasi hanya terkirim saat app absensi sedang dibuka di HP (web/PWA tidak bisa melacak GPS di latar belakang). Diperbarui tiap ±1 menit.</div>
    </div>
  );
};

export default TechPresence;
